import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchAuditEvents, type AdminAuditEvent, type AdminAuditEventsFilters, type AdminAuditEventsPage } from 'api/adminAuditApi';
import { ApiError } from 'utils/unwrapResponse';

/** Query key prefix PR8 invalidates after a technician write lands a new event (J2). */
export const ADMIN_AUDIT_QUERY_KEY = ['admin-audit'] as const;

/**
 * Cursor-paginated read of the action journal (J11, CA14). `enabled` keeps it from firing without
 * `dashboard:audit:view` (CA10); a 403 never retries (CA24) since the permission will not
 * reappear mid-session, unlike a transient failure.
 */
export const useAdminAuditEvents = (filters: AdminAuditEventsFilters, enabled = true) => {
  const query = useInfiniteQuery<AdminAuditEventsPage>({
    queryKey: [...ADMIN_AUDIT_QUERY_KEY, filters],
    queryFn: ({ pageParam }) => fetchAuditEvents({ ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 403) && failureCount < 2,
    enabled,
  });

  const firstPage = query.data?.pages[0];
  const events: AdminAuditEvent[] = query.data?.pages.flatMap((page) => page.events) ?? [];

  return {
    ...query,
    events,
    total: firstPage?.total ?? 0,
    since: firstPage?.since ?? null,
    retentionMonths: firstPage?.retentionMonths ?? null,
  };
};
